import {
  createClient,
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ConnectionState,
  ConnectionDisconnectedReason,
  UID,
  ClientRole,
  ClientRoleOptions,
  ILocalTrack,
} from 'agora-rtc-react';
import { EventEmitter } from 'events';
import { liveEndpoints } from '../api/apiProvider';
import { store } from '../../store/store';
import { AGORA_APP_ID } from '../../loadEnv';
import { EXPIRED_TOKEN_TIME } from '../../common/constants/globalConstants';

export class RtcEngine extends EventEmitter {
  instance?: IAgoraRTCClient;
  channelId?: string;

  constructor() {
    super();
    this.instance = createClient({
      mode: 'live',
      codec: 'vp8',
    })();

    this.subscribeClientEvents();
  }

  subscribeClientEvents(): void {
    // user joinned channel
    this.instance?.on('user-joined', (user: IAgoraRTCRemoteUser) => {
      this.emit('user-joined', user);
    });

    // user leave channel
    this.instance?.on(
      'user-left',
      (user: IAgoraRTCRemoteUser, reason: string) => {
        this.emit('user-left', user, reason);
      },
    );

    // connection change
    this.instance?.on(
      'connection-state-change',
      (
        curState: ConnectionState,
        revState: ConnectionState,
        reason: ConnectionDisconnectedReason | undefined,
      ) => {
        this.emit('connection-state-change', curState, revState, reason);
      },
    );

    // user publish video or audio stream
    this.instance?.on(
      'user-published',
      (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
        this.emit('user-published', user, mediaType);
      },
    );

    // user remove video or audio stream
    this.instance?.on(
      'user-unpublished',
      (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
        this.emit('user-unpublished', user, mediaType);
      },
    );

    // listen for volume indicator
    this.instance?.on(
      'volume-indicator',
      (result: { level: number; uid: UID }) => {
        this.emit('volume-indicator', result);
      },
    );

    this.instance?.on(
      'user-left',
      (user: IAgoraRTCRemoteUser, reason: string) => {
        this.emit('user-left', user, reason);
      },
    );
    this.instance?.on('token-privilege-will-expire', () => {
      this.emit('token-privilege-will-expire');
    });
    this.instance?.on('token-privilege-did-expire', () => {
      this.emit('token-privilege-did-expire');
    });
  }

  // update client role: Audience or Host
  // Note: This role must be set before publishing the track
  setClientRole(role: ClientRole, option?: ClientRoleOptions): void {
    this.instance?.setClientRole(role, option);
  }

  // join RTC channel
  async joinChannel(
    channelId: string,
    liveUserId: number | string | null,
    role: 'subscriber' | 'publisher' = 'publisher',
    liveId?: string,
  ): Promise<UID | undefined> {
    if (!liveId) {
      liveId = channelId;
    }
    const getLiveTokenRTC = liveEndpoints.getLiveTokenRTC.initiate;
    let token: string | null = null;
    if (!liveUserId) {
      liveUserId = Math.floor(Math.random() * 1000000000);
    }
    try {
      const res = await store
        .dispatch(
          getLiveTokenRTC({
            channelId,
            expire: EXPIRED_TOKEN_TIME,
            role,
            wildcard: false,
            uid: String(liveUserId),
            liveId: liveId,
          }),
        )
        .unwrap();
      token = res.token;
    } catch (error) {
      console.error('getLiveTokenRTC error: ', error);
    }

    if (this.channelId != channelId) {
      this.channelId = channelId;
      return this.instance
        ?.join(AGORA_APP_ID, channelId, token || null, liveUserId)
        .then((uid) => {
          if (token) {
            this.removeAllListeners('token-privilege-will-expire');
            this.removeAllListeners('token-privilege-did-expire');
            this.on('token-privilege-will-expire', () => {
              this.refetchToken(channelId, liveUserId!, role, liveId!);
            });
            this.on('token-privilege-did-expire', () => {
              this.refetchToken(channelId, liveUserId!, role, liveId!, true);
            });
          }
          return uid;
        });
    }
  }

  async refetchToken(
    channelId: string,
    liveUserId: number | string,
    role: 'subscriber' | 'publisher' = 'publisher',
    liveId: string,
    isDidExpired = false,
  ) {
    try {
      const getLiveTokenRTC = liveEndpoints.getLiveTokenRTC.initiate;
      const res = await store
        .dispatch(
          getLiveTokenRTC({
            channelId,
            expire: EXPIRED_TOKEN_TIME,
            role,
            wildcard: false,
            uid: String(liveUserId),
            liveId,
          }),
        )
        .unwrap();
      const token = res.token;
      if (isDidExpired) {
        await this.instance?.join(
          AGORA_APP_ID,
          channelId,
          token || null,
          liveUserId,
        );
      } else {
        await this.instance?.renewToken(token);
      }
    } catch (error) {
      console.error('refetch token rtc failed: ', error);
    }
  }

  // publish track as host role
  publishStream(tracks: ILocalTrack | ILocalTrack[]): void {
    this.instance?.publish(tracks);
  }

  // unpublish track as host role
  async unpublishStream(tracks: ILocalTrack | ILocalTrack[]): Promise<void> {
    await this.instance?.unpublish(tracks);
  }

  // leave current channel
  async leaveChannel(): Promise<void> {
    await this.instance?.leave();
    this.channelId = undefined;
  }

  // destroy rtc instance
  destroy(): void {
    this.instance?.removeAllListeners();
    this.instance?.leave();
    delete this.instance;
  }
}
